import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, MoreHorizontal, ExternalLink, BedDouble, LogIn, LogOut, Wallet, StickyNote, Ban, Printer } from "lucide-react";
import StatusPill from "./StatusPill";
import SourceLogo from "./SourceLogo";
import { addDays, toYMD, formatCurrency, formatShortDate } from "@/lib/format";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

/**
 * Reservations workspace — left-hand operational panel.
 * Tabs: Arrivals / Departures / In-house / Balance.
 * A compact search filters by guest name.
 *
 * Actions available on each row respect current reservation state
 * (assign only if unassigned, check-in only if confirmed, etc.).
 */
export default function ReservationsPanel({ reservations, rooms, currency, onQuickAction }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tab, setTab] = useState("arrivals");
    const [when, setWhen] = useState("today");   // today | tomorrow
    const [query, setQuery] = useState("");

    const today    = toYMD(new Date());
    const tomorrow = toYMD(addDays(new Date(today), 1));
    const targetDate = when === "today" ? today : tomorrow;
    const active = (r) => !["cancelled", "no_show"].includes(r.status);
    const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);
    const showWhen = tab === "arrivals" || tab === "departures";

    // ── Rows for the currently selected tab
    const rows = useMemo(() => {
        let base = reservations;
        if (tab === "arrivals")   base = base.filter((r) => r.check_in  === targetDate && active(r));
        else if (tab === "departures") base = base.filter((r) => r.check_out === targetDate && active(r));
        else if (tab === "in_house")   base = base.filter((r) => r.status === "checked_in");
        else if (tab === "balance") {
            base = base.filter((r) => {
                if (["cancelled", "no_show"].includes(r.status)) return false;
                const bal = (r.total_amount || 0) - (r.paid_amount || 0);
                return bal > 0.01 && ["pending", "partial"].includes(r.payment_status || "");
            });
        }
        const qLower = query.trim().toLowerCase();
        if (qLower) {
            base = base.filter((r) => {
                const name = `${r.guest_first_name || ""} ${r.guest_last_name || ""}`.toLowerCase();
                return name.includes(qLower) || (r.confirmation_number || "").toLowerCase().includes(qLower);
            });
        }
        return base;
    }, [reservations, tab, targetDate, query]);

    const tabs = [
        { key: "arrivals",   label: t("dashboard.arrivals_tab"),   count: reservations.filter((r) => r.check_in  === today && active(r)).length },
        { key: "departures", label: t("dashboard.departures_tab"), count: reservations.filter((r) => r.check_out === today && active(r)).length },
        { key: "in_house",   label: t("dashboard.in_house_tab"),   count: reservations.filter((r) => r.status === "checked_in").length },
        { key: "balance",    label: t("dashboard.balance_tab"),    count: reservations.filter((r) => {
            if (["cancelled", "no_show"].includes(r.status)) return false;
            return (r.total_amount || 0) - (r.paid_amount || 0) > 0.01;
        }).length },
    ];

    // ── Contextual actions per row + tab
    const actionsFor = (r) => {
        const list = [{ key: "open", icon: ExternalLink, label: t("dashboard.action_open"), run: () => navigate(`/reservations?id=${r.id}`) }];
        if (!r.room_id) list.push({ key: "assign", icon: BedDouble, label: t("dashboard.action_assign_room"), run: () => navigate(`/reservations?id=${r.id}&assign=1`) });
        if (["confirmed", "pending"].includes(r.status) && r.room_id) list.push({ key: "checkin", icon: LogIn, label: t("dashboard.action_check_in"), run: () => onQuickAction?.("check_in", r) });
        if (r.status === "checked_in")                                 list.push({ key: "checkout", icon: LogOut, label: t("dashboard.action_check_out"), run: () => onQuickAction?.("check_out", r) });
        const bal = (r.total_amount || 0) - (r.paid_amount || 0);
        if (bal > 0.01) list.push({ key: "payments", icon: Wallet, label: t("dashboard.action_view_payments"), run: () => navigate(`/reservations?id=${r.id}&tab=payments`) });
        list.push({ key: "note", icon: StickyNote, label: t("dashboard.action_add_note"), run: () => navigate(`/reservations?id=${r.id}&tab=notes`) });
        list.push({ key: "print", icon: Printer, label: t("dashboard.action_print"), run: () => window.open(`/api/reservations/${r.id}/folio.pdf`, "_blank") });
        if (!["cancelled", "checked_out", "no_show"].includes(r.status)) {
            list.push({ key: "cancel", icon: Ban, label: t("dashboard.action_cancel"), tone: "danger", run: () => onQuickAction?.("cancel", r) });
        }
        return list;
    };

    return (
        <section className="rounded-[10px] border border-border bg-card" data-testid="reservations-panel">
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-3.5">
                <h2 className="text-[15px] font-semibold text-foreground">{t("dashboard.reservations")}</h2>
                <span className="text-[12px] tabular-nums text-muted-foreground">{rows.length}</span>
            </div>

            {/* Tabs */}
            <div className="mt-2 flex items-center gap-5 border-b border-border px-4 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {tabs.map((tb) => {
                    const isActive = tb.key === tab;
                    return (
                        <button
                            key={tb.key}
                            onClick={() => setTab(tb.key)}
                            data-testid={`resv-tab-${tb.key}`}
                            className={`relative -mb-px flex items-center gap-1.5 whitespace-nowrap pb-2.5 pt-1 text-[13px] transition ${
                                isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/90"
                            }`}
                        >
                            <span className="font-medium">{tb.label}</span>
                            <span className={`rounded-full px-1.5 text-[10.5px] font-semibold tabular-nums ${
                                isActive ? "bg-[#ff7b02]/10 text-[#ff7b02]" : "bg-muted text-muted-foreground"
                            }`}>
                                {tb.count}
                            </span>
                            {isActive && <span className="absolute inset-x-0 bottom-[-1px] h-[2px] rounded-t bg-[#ff7b02]" />}
                        </button>
                    );
                })}
            </div>

            {/* Sub-filters: date pills + search */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                {showWhen ? (
                    <div className="inline-flex rounded-md bg-muted p-0.5">
                        {[
                            { k: "today",    l: t("dashboard.today") },
                            { k: "tomorrow", l: t("dashboard.tomorrow") },
                        ].map((s) => (
                            <button
                                key={s.k}
                                onClick={() => setWhen(s.k)}
                                data-testid={`resv-when-${s.k}`}
                                className={`rounded px-3 py-1 text-[12px] font-medium transition ${
                                    when === s.k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                {s.l}
                            </button>
                        ))}
                    </div>
                ) : <div />}

                <div className="relative min-w-[180px] flex-1 max-w-[260px]">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("dashboard.search_guest")}
                        data-testid="resv-search"
                        className="h-8 w-full rounded-md border border-border bg-muted/60 pl-7 pr-2 text-[12.5px] outline-none placeholder:text-muted-foreground/70 focus:border-[#ff7b02]/40 focus:bg-card focus:ring-2 focus:ring-[#ff7b02]/10"
                    />
                </div>
            </div>

            {/* Table */}
            {rows.length === 0 ? (
                <EmptyState tab={tab} when={when} />
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-[720px] w-full text-[12.5px]" data-testid="resv-table">
                        <thead>
                            <tr className="border-y border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                                {tab === "arrivals"   && <ArrivalsCols   t={t} />}
                                {tab === "departures" && <DeparturesCols t={t} />}
                                {tab === "in_house"   && <InHouseCols    t={t} />}
                                {tab === "balance"    && <BalanceCols    t={t} />}
                                <th className="py-2 pr-4 text-right"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/60">
                            {rows.slice(0, 20).map((r) => (
                                <Row
                                    key={r.id}
                                    r={r}
                                    tab={tab}
                                    room={roomById[r.room_id]}
                                    currency={currency}
                                    actions={actionsFor(r)}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Footer link */}
            {rows.length > 20 && (
                <div className="border-t border-border/60 px-4 py-2.5 text-right">
                    <Link
                        to="/reservations"
                        className="text-[12.5px] font-medium text-[#ff7b02] hover:underline"
                    >
                        {t("dashboard.view_all")} →
                    </Link>
                </div>
            )}
        </section>
    );
}

// ─────────────── column presets ───────────────
const th = "px-4 py-2 font-medium";
function ArrivalsCols({ t })   { return <><th className={th}>{t("dashboard.col_guest")}</th><th className={th}>{t("dashboard.col_reservation_no")}</th><th className={th}>{t("dashboard.col_room")}</th><th className={th}>{t("dashboard.col_room_type")}</th><th className={th}>{t("dashboard.col_source")}</th><th className={th}>{t("dashboard.col_status")}</th></>; }
function DeparturesCols({ t }) { return <><th className={th}>{t("dashboard.col_guest")}</th><th className={th}>{t("dashboard.col_room")}</th><th className={th}>{t("dashboard.col_check_out")}</th><th className={th}>{t("dashboard.col_balance")}</th><th className={th}>{t("dashboard.col_status")}</th></>; }
function InHouseCols({ t })    { return <><th className={th}>{t("dashboard.col_guest")}</th><th className={th}>{t("dashboard.col_room")}</th><th className={th}>{t("dashboard.col_check_in")}</th><th className={th}>{t("dashboard.col_check_out")}</th><th className={th}>{t("dashboard.col_nights")}</th><th className={th}>{t("dashboard.col_balance")}</th></>; }
function BalanceCols({ t })    { return <><th className={th}>{t("dashboard.col_guest")}</th><th className={th}>{t("dashboard.col_reservation")}</th><th className={th}>{t("dashboard.col_room")}</th><th className={th}>{t("dashboard.col_total")}</th><th className={th}>{t("dashboard.col_paid")}</th><th className={th}>{t("dashboard.col_balance")}</th><th className={th}>{t("dashboard.col_deadline")}</th></>; }

// ─────────────── row (per tab) ───────────────
function Row({ r, tab, room, currency, actions }) {
    const td = "px-4 py-2.5 align-middle";
    const guest = (
        <Link
            to={`/reservations?id=${r.id}`}
            className="font-medium text-foreground hover:underline"
            data-testid={`row-guest-${r.id}`}
        >
            {r.guest_first_name} {r.guest_last_name}
        </Link>
    );
    const bal = (r.total_amount || 0) - (r.paid_amount || 0);
    return (
        <tr className="group hover:bg-muted/60">
            {tab === "arrivals" && (
                <>
                    <td className={td}>{guest}</td>
                    <td className={`${td} font-mono text-[11.5px] text-muted-foreground`}>{r.confirmation_number || `AK-${(r.id || "").slice(0, 6).toUpperCase()}`}</td>
                    <td className={`${td} text-foreground`}>{room ? room.number : <span className="text-amber-700">—</span>}</td>
                    <td className={`${td} text-muted-foreground`}>{room?.room_type || "—"}</td>
                    <td className={td}><SourceLogo source={r.source || "direct"} /></td>
                    <td className={td}><StatusPill status={r.status} /></td>
                </>
            )}
            {tab === "departures" && (
                <>
                    <td className={td}>{guest}</td>
                    <td className={`${td} text-foreground`}>{room ? room.number : "—"}</td>
                    <td className={`${td} font-mono text-[11.5px] text-muted-foreground`}>{r.check_out_time || "11:00"}</td>
                    <td className={td}>
                        {bal > 0.01
                            ? <span className="font-medium text-rose-700">{formatCurrency(bal, currency)}</span>
                            : <span className="text-muted-foreground">{formatCurrency(0, currency)}</span>}
                    </td>
                    <td className={td}><StatusPill status={r.status} /></td>
                </>
            )}
            {tab === "in_house" && (
                <>
                    <td className={td}>{guest}</td>
                    <td className={`${td} text-foreground`}>{room ? room.number : "—"}</td>
                    <td className={`${td} tabular-nums text-muted-foreground`}>{formatShortDate(r.check_in)}</td>
                    <td className={`${td} tabular-nums text-muted-foreground`}>{formatShortDate(r.check_out)}</td>
                    <td className={`${td} tabular-nums text-foreground`}>{nightsBetween(r.check_in, r.check_out)}</td>
                    <td className={td}>{bal > 0.01
                        ? <span className="font-medium text-rose-700">{formatCurrency(bal, currency)}</span>
                        : <span className="text-muted-foreground">{formatCurrency(0, currency)}</span>}</td>
                </>
            )}
            {tab === "balance" && (
                <>
                    <td className={td}>{guest}</td>
                    <td className={`${td} font-mono text-[11.5px] text-muted-foreground`}>{r.confirmation_number || `AK-${(r.id || "").slice(0, 6).toUpperCase()}`}</td>
                    <td className={`${td} text-foreground`}>{room ? room.number : "—"}</td>
                    <td className={`${td} tabular-nums text-foreground`}>{formatCurrency(r.total_amount || 0, currency)}</td>
                    <td className={`${td} tabular-nums text-muted-foreground`}>{formatCurrency(r.paid_amount || 0, currency)}</td>
                    <td className={`${td} tabular-nums font-medium text-rose-700`}>{formatCurrency(bal, currency)}</td>
                    <td className={`${td} tabular-nums text-muted-foreground`}>{formatShortDate(r.check_in)}</td>
                </>
            )}
            <td className={`${td} pr-4 text-right`}>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid={`row-actions-${r.id}`}
                            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground/70 hover:bg-muted hover:text-foreground/90"
                        >
                            <MoreHorizontal size={14} />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                        {actions.map((a) => {
                            const Icon = a.icon;
                            return (
                                <DropdownMenuItem
                                    key={a.key}
                                    onClick={a.run}
                                    className={a.tone === "danger" ? "text-rose-600 focus:text-rose-700" : ""}
                                >
                                    <Icon size={13} className="mr-2 opacity-70" /> {a.label}
                                </DropdownMenuItem>
                            );
                        })}
                    </DropdownMenuContent>
                </DropdownMenu>
            </td>
        </tr>
    );
}

function EmptyState({ tab, when }) {
    const { t } = useTranslation();
    const msg = tab === "arrivals"   ? (when === "today" ? t("dashboard.no_arrivals")   : t("dashboard.no_arrivals"))
              : tab === "departures" ? (when === "today" ? t("dashboard.no_departures") : t("dashboard.no_departures"))
              : tab === "in_house"   ? t("dashboard.no_in_house")
              :                        t("dashboard.no_outstanding");
    return <div className="px-4 py-10 text-center text-[12.5px] text-muted-foreground/70">{msg}</div>;
}

function nightsBetween(a, b) {
    try {
        return Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
    } catch {
        return 0;
    }
}
